import React, { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import cn from "classnames";

// Mui Components
import {
    TableRow,
    TableCell,
    IconButton,
    Collapse,
    Box,
    Typography,
    CircularProgress,
    ClickAwayListener,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import RestoreIcon from "@mui/icons-material/Restore";

// Utils
import { format } from "date-fns";

// Enums
import icons from "enums/icons";

// Styles
import useUsersTableStyles from "./UsersTable.styles";

//  Components
import CustomIcon from "components/CustomIcon";
import { InfoWithIcon } from "../InfoWithIcon";
import { ConvertedClientHubUser } from "pages/ClientHub/adapters/converters";
import PersonWorkHistory from "components/PersonWorkHistory";

// Types
import { TPersonInfo } from "components/Team/Team.types";

const initialPersonInfo = {
    role: "",
    id: -1,
    info: undefined,
};

const UsersTableRow = ({
    data,
    Checkbox,
    RoleSelectInput,
    RemoveUserButton,
}: {
    data: ConvertedClientHubUser;
    Checkbox?: React.ReactNode;
    RoleSelectInput?: React.ReactNode;
    RemoveUserButton?: React.ReactNode;
}) => {
    const intl = useIntl();
    const classes = useUsersTableStyles();

    const [shouldExpand, setShouldExpand] = useState(false);

    const [toggleShowWorkHistory, setToggleShowWorkHistory] = useState(false);
    const [WHAnchorEl, setWHAnchorEl] = useState<HTMLElement | null>(null);
    const [personInfo, setPersonInfo] = useState<TPersonInfo>(initialPersonInfo);

    const isBusy = data.status === "saving" || data.status === "removing";

    const handleToggleExpand = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        event.stopPropagation();
        setShouldExpand(!shouldExpand);
    };

    const handleClickLinkedIn = (ev: React.SyntheticEvent) => {
        ev.stopPropagation();
    };

    const handleShowWorkHistory = (event: React.MouseEvent<HTMLElement>) => {
        if (!data.personId) return;

        setToggleShowWorkHistory(true);
        setPersonInfo({
            role: data?.position || "",
            id: data.personId,
        });
        setWHAnchorEl(event.currentTarget);
    };

    const handleCloseWorkHistory = () => {
        setToggleShowWorkHistory(false);
        setWHAnchorEl(null);
    };

    return (
        <Fragment>
            <TableRow
                onClick={(event) => handleToggleExpand(event)}
                className={cn({
                    [classes.removingStatus]: data.status === "removing",
                    [classes.savingStatus]: data.status === "saving",
                })}
            >
                {Checkbox && <TableCell padding="checkbox">{Checkbox}</TableCell>}
                <TableCell>
                    <InfoWithIcon
                        startSlot={
                            <IconButton onClick={(event) => handleToggleExpand(event)}>
                                <InfoIcon
                                    sx={(theme) => ({
                                        fontSize: 18,
                                        color: theme.palette.info.main,
                                    })}
                                />
                            </IconButton>
                        }
                        entityName={data.name}
                        primary={data.company}
                        secondary={data.position}
                    />
                </TableCell>
                {RoleSelectInput && <TableCell>{RoleSelectInput}</TableCell>}
                {data.activeOn && <TableCell>{format(new Date(data.activeOn), "dd/MM/yyyy")}</TableCell>}
                <TableCell>{isBusy && <CircularProgress color="primary" size={20} />}</TableCell>
                {RemoveUserButton && <TableCell align="right">{RemoveUserButton}</TableCell>}
            </TableRow>
            <TableRow className={classes.expandableRow}>
                <TableCell colSpan={5}>
                    <Collapse in={shouldExpand} timeout="auto" unmountOnExit>
                        <Box display="flex" alignItems="center" py={0.6} px={0}>
                            <Box display="flex" alignItems="center" mr={2}>
                                <IconButton
                                    size="small"
                                    sx={{ color: "primary.dark" }}
                                    disabled={!data.personId}
                                    onClick={handleShowWorkHistory}
                                >
                                    <RestoreIcon />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    sx={{ color: "secondary.main" }}
                                    aria-label="linkedin button"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    disabled={!data.linkedInId}
                                    href={data.linkedInId ? `https://www.linkedin.com/in/${data.linkedInId}/` : "#"}
                                    onClick={handleClickLinkedIn}
                                >
                                    <CustomIcon icon={icons.linkedin} sx={{ fontSize: 20 }} />
                                </IconButton>
                            </Box>
                            <Box display="flex" alignItems="center" mr={2}>
                                <Typography mr={1}>
                                    {intl.formatMessage({ id: "hubManagement.client.userTable.email" })}:
                                </Typography>
                                <Typography>{data.email}</Typography>
                            </Box>
                            <IconButton
                                className={classes.toggleCloseBtn}
                                onClick={(event) => handleToggleExpand(event)}
                            >
                                <CustomIcon icon={icons.close} />
                            </IconButton>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>

            {toggleShowWorkHistory ? (
                <>
                    <ClickAwayListener onClickAway={handleCloseWorkHistory}>
                        <PersonWorkHistory
                            role={""}
                            personId={personInfo.id}
                            anchorEl={WHAnchorEl}
                            onClose={handleCloseWorkHistory}
                            open={toggleShowWorkHistory}
                        />
                    </ClickAwayListener>
                </>
            ) : null}
        </Fragment>
    );
};

export default UsersTableRow;
